<template>
  <form ref="form" @submit.prevent="handleSubmit">
    <v-container class="main-container">
      <v-row>
        <v-col xl="6" lg="7" md="7" sm="7" cols="12">
          <v-card class="deposit-form" :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Transfer Method</legend>
                  <v-text-field label="Crypto Transfer" disabled placeholder="Crypto Transfer" single-line solo
                    hide-details readonly class="custom-select">
                  </v-text-field>
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Deposit to</legend>
                  <v-select v-model="selectedAccount" :items="formattedAccounts" item-text="display"
                    item-value="trading_account_no" solo append-icon="mdi-chevron-down" label="Select One"
                    class="custom-select" hide-details @change="handleAccountChange">
                  </v-select>
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <fieldset>
                  <legend>Select Crypto Network</legend>
                  <v-select v-model="selectedNetwork" solo :items="networks" item-text="network"
                    item-value="renamedNetwork" append-icon="mdi-chevron-down" label="Select One" class="custom-select"
                    @change="handleNetworkChange">
                  </v-select>
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" :md="currency.name !== 'USD' ? 6 : 12">
                <fieldset>
                  <legend>Amount in (USD)</legend>
                  <v-text-field v-model="depositAmount" label="$" type="number" single-line solo hide-details
                    class="custom-select" min="1" :rules="[rules.required, rules.minAmount]"></v-text-field>
                </fieldset>
              </v-col>
              
              <v-col cols="12" sm="12" md="6" v-if="currency.name !== 'USD'">
                <fieldset>
                  <legend>Amount in <span :style="{ color: currency.color }">({{ currency.name }})</span></legend>
                  <v-text-field readonly v-model="currencyAmount" :label="currency.symbol" type="number" single-line solo hide-details
                    class="custom-select text-muted"></v-text-field>
                </fieldset>
              </v-col>

              <v-col cols="12" sm="12" md="12" v-if="needSlip == 1">
                <div class="input-file">
                  <span>Upload Pay.Proof</span>
                  <v-file-input v-model="bankSlip" label="Choose file" filled prepend-icon="mdi-file-image"
                    hide-details>
                  </v-file-input>
                </div>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <v-card-title class="title px-0 py-0">Instruction:</v-card-title>
                <ol class="instruction">
                  <li>
                    Payments from third parties are not accepted. The name of
                    the sender must match the account holder's name.
                  </li>
                  <li>
                    Broker accepts wire transfers in USD. Funds sent in a
                    different currency will be converted at the current exchange rate.
                  </li>
                </ol>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <v-btn type="submit" tile elevation="0" class="ma-0 text-capitalize btn-account d-block"
                  color="greenMain" :loading="loading" :disabled="!isFormValid || loading">
                  Deposit
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col xl="6" lg="5" md="5" sm="5" cols="12">
          <v-card v-if="selectedNetwork" class="elevation-0 rounded-0 deposit-form-card mt-2"
            :color="$vuetify.theme.dark ? '#162334' : '#F1F7FB'">
            <v-card-text class="content">
              <h3 class="title">Broker <strong>{{ selectedNetworkName }}</strong> Address</h3>
              <span class="d-inline-block text-truncate" ref="addressText">{{ selectedNetworkAddress }}</span>
              <v-btn tile elevation="0" class="ma-0 text-capitalize btn-account d-block" color="greenMain"
                @click="copyAddress">
                {{ copied ? 'Copied!' : 'Copy Address' }}
              </v-btn>
            </v-card-text>
            <v-card-text>
              <v-img :src="qrCodeUrl" alt="QR Code" width="200" height="200">
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { CRYPTO__ACTIONS, DEPOSITMETHODS__ACTIONS } from "@/store/action-types";
import toast from "@/services/Notification";

export default {
  name: 'CryptoDepositForm',

  data() {
    return {
      payment_id: null,
      selectedAccount: null,
      selectedNetwork: null,
      depositAmount: '',
      currencyAmount: '',
      currency: {
        rate: 1,
        name: "USD",
        symbol: "$",
        color:"#fff"
      },
      bankSlip: null,
      loading: false,
      copied: false,
      baseUrl: 'https://api.qrserver.com/v1/create-qr-code/',
      rules: {
        required: value => !!value || 'This field is required',
        minAmount: value => value > 0 || 'Amount must be greater than 0'
      }
    };
  },

  computed: {
    ...mapState("crypto", ["networks"]),
    ...mapState("deposit", ["accounts","needSlip"]),

    formattedAccounts() {
      return this.accounts.map(account => ({
        display: `${account.trading_account_no} (${account.account_type_display}) ${account.Balance} ${account.currency_name}`,
        trading_account_no: account.trading_account_no,
        ...account
      }));
    },

    selectedAccountDetails() {
      return this.accounts.find(acc => acc.trading_account_no === this.selectedAccount);
    },

    selectedNetworkDetails() {
      return this.networks.find(network => network.renamedNetwork === this.selectedNetwork) || {};
    },

    selectedNetworkAddress() {
      return this.selectedNetworkDetails.address || '';
    },

    selectedNetworkName() {
      return this.selectedNetworkDetails.network || '';
    },

    qrCodeUrl() {
      return this.selectedNetworkAddress
        ? `${this.baseUrl}?size=200x200&data=${encodeURIComponent(this.selectedNetworkAddress)}`
        : '';
    },

    isFormValid() {
      return this.selectedAccount &&
        this.selectedNetwork &&
        this.depositAmount > 0  
    }
  },

  watch: {
    depositAmount(newAmount) {
      if (this.currency && this.currency.rate) {
        this.currencyAmount = (newAmount * this.currency.rate).toFixed(2);
      } else {
        this.currencyAmount = newAmount;
      }
    }
  },

  methods: {
    ...mapActions("crypto", [CRYPTO__ACTIONS.NETWORKS]),
    ...mapActions("deposit", [
      DEPOSITMETHODS__ACTIONS.DEPOSITACCOUNTS,
      DEPOSITMETHODS__ACTIONS.CREATECRYPTODEPOSIT,
      DEPOSITMETHODS__ACTIONS.NEEDSLIP,
    ]),

    handleAccountChange() {
      this.depositAmount = '';
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      let account_details = this.accounts.find(acc => acc.trading_account_no === this.selectedAccount);
      this.currencyAmount = ''; 
      if (account_details) {
        this.currency.rate = account_details.currency_rate;
        this.currency.name = account_details.currency_name;
        this.currency.symbol = account_details.currency_symbol;
        this.currency.color = account_details.symbol_color;
      }
    },

    handleNetworkChange() {
      this.copied = false;
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },

    async copyAddress() {
      if (!this.selectedNetworkAddress) return;

      try {
        await navigator.clipboard.writeText(this.selectedNetworkAddress);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },

    validateForm() {
      if (!this.selectedAccount) {
        toast.error('Please select an account');
      }
      if (!this.selectedNetwork) {
        toast.error('Please select a network');
      }
      if (!this.depositAmount || this.depositAmount <= 0) {
        toast.error('Please enter a valid deposit amount');
      }
      if (!this.bankSlip && this.needSlip == 1) {
        toast.error('Please upload payment proof');
      }
    },

    async handleSubmit() {
      try {
        this.validateForm();
        this.loading = true;

        const formData = new FormData();
        formData.append('trading_account_no', this.selectedAccount);
        formData.append('amount', this.depositAmount);
        formData.append('payment_slip', this.bankSlip);
        formData.append('payment_id', this.payment_id);
        formData.append('network', this.selectedNetwork);
        formData.append('wallet_address', this.selectedNetworkAddress);

        await this[DEPOSITMETHODS__ACTIONS.CREATECRYPTODEPOSIT](formData);

        this.resetForm();
      } catch (error) {
        console.error('Deposit submission error:', error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.$nextTick(() => {
        this.selectedAccount = null;
        this.selectedNetwork = null;
        this.depositAmount = '';
        this.bankSlip = null;
        this.copied = false;

        if (this.$refs.form) {
          this.$refs.form.reset();
        }

        const fileInput = this.$refs.fileInput;
        if (fileInput) {
          fileInput.reset();
        }
      });
    }
  },

  async mounted() {
    try {
      this.payment_id = this.$route.query.payment_id;
      await Promise.all([
        this[CRYPTO__ACTIONS.NETWORKS](),
        this[DEPOSITMETHODS__ACTIONS.DEPOSITACCOUNTS]()
      ]);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    }
  },

  async created(){
    await this[DEPOSITMETHODS__ACTIONS.NEEDSLIP]();
  }
};
</script>